import { useState } from "react";
import { useReveal } from "../../hooks/useReveal";
import { useVantaHalo } from "../../hooks/useVantaHalo";
import { useI18n } from "../../i18n/useI18n";
import { computeChart, type ChartResult } from "../../lib/ephemeris/chart";
import { GlassCard } from "../ui/GlassCard";
import { Input } from "../ui/Input";
import { DateInput } from "../ui/DateInput";
import { TimeInput } from "../ui/TimeInput";
import { CountrySelect } from "../ui/CountrySelect";
import { Button } from "../ui/Button";
import { ChartModal } from "./ChartModal";
import { DynamicImage } from "../DynamicImage";
import { PAGE_IMAGE_SLOTS } from "../../lib/page-image-defaults";

const heroImage = PAGE_IMAGE_SLOTS.find((s) => s.pagePath === "/" && s.sectionName === "hero")!;

export function Hero() {
  const scope = useReveal<HTMLElement>();
  const bg = useVantaHalo();
  const { t } = useI18n();
  const [form, setForm] = useState({ dob: "", time: "", city: "", countryCode: "DE" });
  const [result, setResult] = useState<ChartResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(await computeChart(form)); // real sidereal calc — see lib/ephemeris/chart.ts
    } catch (err) {
      setError(err instanceof Error ? err.message : "Berechnung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="top" ref={scope} className="relative min-h-screen overflow-hidden">
      {/* Beige Aura base — warm gold-beige radial glows on alabaster */}
      <div aria-hidden="true" className="absolute inset-0 -z-20 bg-[radial-gradient(60%_55%_at_78%_15%,rgba(212,175,55,0.22),transparent_60%),radial-gradient(70%_65%_at_18%_88%,rgba(232,200,106,0.20),transparent_62%),radial-gradient(90%_90%_at_50%_50%,rgba(250,244,228,0.9),#FDFCF9_75%)]" />
      {/* Beige aura accent layer above Vanta so the warm glow always reads;
          extra warm wash bottom-left keeps the palette 100% beige/gold. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-[5] bg-[radial-gradient(45%_50%_at_80%_20%,rgba(212,175,55,0.14),transparent_60%),radial-gradient(55%_60%_at_15%_88%,rgba(233,205,150,0.22),transparent_62%)]" />
      {/* Vanta HALO */}
      <div ref={bg} className="absolute inset-0 -z-10 h-full w-full" aria-hidden="true" />
      {/* Celestial accents ABOVE Vanta (Vanta fills its own bg, so tint on top):
          soft Royal Blue lower-left + Muted Gold upper-right. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-[5] bg-[radial-gradient(45%_50%_at_18%_82%,rgba(30,58,138,0.22),transparent_60%),radial-gradient(45%_50%_at_82%_18%,rgba(212,175,55,0.20),transparent_60%)]"
      />

      <div className="container-lux relative z-10 grid min-h-screen items-center gap-10 bg-transparent pt-28 pb-16 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="max-w-xl">
          <DynamicImage
            pagePath={heroImage.pagePath}
            sectionName={heroImage.sectionName}
            defaultImage={heroImage.defaultImage}
            alt={heroImage.alt}
            wrapperClassName="mb-6 h-40 w-full rounded-3xl shadow-glow"
            className="h-full w-full rounded-3xl object-cover"
          />
          <p className="eyebrow mb-5" data-reveal>{t.hero.eyebrow}</p>
          <h1 className="text-4xl font-semibold leading-[1.06] sm:text-5xl md:text-[3.4rem]" data-reveal>
            {t.hero.headline}
            <br />
            <em className="italic text-gold">{t.hero.headlineAccent}</em>
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate2/80" data-reveal>
            {t.hero.subtitle}
          </p>
        </div>

        <div className="flex justify-center lg:justify-end" data-reveal>
          <GlassCard className="w-full max-w-md p-7 md:p-8">
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <div className="text-center">
                <p className="eyebrow mb-2">{t.form.badge}</p>
                <h2 className="text-2xl font-semibold">{t.form.title}</h2>
              </div>
              <DateInput id="dob" label={t.form.dob} required value={form.dob} onChange={(dob) => setForm({ ...form, dob })} />
              <TimeInput id="time" required value={form.time} onChange={(time) => setForm({ ...form, time })} />
              <Input id="city" label={t.form.city} placeholder={t.form.cityPh} required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <CountrySelect
                id="country"
                label={t.form.country}
                required
                value={form.countryCode}
                onChange={(countryCode) => setForm({ ...form, countryCode })}
              />
              {error && <p className="text-center text-xs text-red-500">{error}</p>}
              <Button type="submit" className="mt-2 w-full" disabled={busy}>
                {busy ? "Berechnet …" : t.form.cta}
              </Button>
              <p className="text-center text-xs text-slate-400">{t.form.trust}</p>
            </form>
          </GlassCard>
        </div>
      </div>

      {result && <ChartModal result={result} onClose={() => setResult(null)} />}
    </section>
  );
}
